import { AlertTriangle, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import AirportInput from "@/components/AirportInput";

export const emptyLeg = () => ({ from_airport: "", to_airport: "", date: "", time: "", pax: 1 });

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "bg-[#0d0f14] border-white/10 h-11";

export default function LegsEditor({ legs, onChange, tripType }) {
  const set = (i, key, val) => onChange(legs.map((l, j) => {
    if (j === i) return { ...l, [key]: val };
    if (key === "date" && j > i && val && l.date && l.date < val) return { ...l, date: "" };
    return l;
  }));
  const timeClash = (i) => {
    const p = legs[i - 1];
    const l = legs[i];
    return i > 0 && l.date && l.date === p.date && l.time && p.time && l.time < p.time;
  };
  const minDate = (i) => legs.slice(0, i).map((l) => l.date).filter(Boolean).sort().pop() || undefined;
  const canAdd = tripType === "multi_leg" && legs.length < 12;
  return (
    <div className="space-y-3" data-testid="legs-editor">
      {legs.map((leg, i) => (
        <div key={i} className="lux-card p-4 sm:p-5 relative" data-testid={`leg-${i}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono-data text-[11px] text-[#5B7FD1]">LEG {String(i + 1).padStart(2, "0")}</span>
            {tripType === "multi_leg" && legs.length > 2 && (
              <button type="button" onClick={() => onChange(legs.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-rose-300" data-testid={`leg-${i}-remove-btn`}>
                <X size={15} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_150px_110px_80px] gap-3">
            <Field label="From (airport / city)" className="col-span-2 md:col-span-1">
              <AirportInput className={inputCls} value={leg.from_airport} onChange={(v) => set(i, "from_airport", v)} placeholder="Code, city, state or country" required testid={`leg-${i}-from-input`} />
            </Field>
            <Field label="To (airport / city)" className="col-span-2 md:col-span-1">
              <AirportInput className={inputCls} value={leg.to_airport} onChange={(v) => set(i, "to_airport", v)} placeholder="Code, city, state or country" required testid={`leg-${i}-to-input`} />
            </Field>
            <Field label="Date">
              <Input type="date" className={inputCls} value={leg.date} min={minDate(i)} onChange={(e) => set(i, "date", e.target.value)} required data-testid={`leg-${i}-date-input`} />
            </Field>
            <Field label="Time">
              <Input type="time" className={inputCls} value={leg.time} onChange={(e) => set(i, "time", e.target.value)} data-testid={`leg-${i}-time-input`} />
            </Field>
            <Field label="Pax">
              <Input type="number" min={1} className={inputCls} value={leg.pax} onChange={(e) => set(i, "pax", e.target.value)} required data-testid={`leg-${i}-pax-input`} />
            </Field>
          </div>
          {timeClash(i) && (
            <p className="mt-3 flex items-center gap-2 text-xs text-amber-300" data-testid={`leg-${i}-time-warning`}>
              <AlertTriangle size={14} className="shrink-0" />
              Leg {i + 1} departs at {leg.time}, before leg {i} departs at {legs[i - 1].time} on the same day. Please check the times.
            </p>
          )}
        </div>
      ))}
      {canAdd && (
        <button type="button" className="frame-btn" onClick={() => onChange([...legs, { ...emptyLeg(), from_airport: legs[legs.length - 1]?.to_airport || "" }])} data-testid="add-leg-btn">
          <Plus size={13} /> Add leg
        </button>
      )}
    </div>
  );
}

export { Field, inputCls };
